import React, { Fragment, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { IconButton } from '@material-ui/core';
import { Edit2 } from 'react-feather';

import { GQLParty, GQLPieceInventory } from '@jewelutions/core-crm-server-api-client-domain';

import { ErrorComponent, LoadingSpinner, JwlTable } from '../../../../components';

import { PieceInventoryUpdateDialog } from '../PieceInventoryUpdateDialog/PieceInventoryUpdateDialog';
import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { ProducerNavigation } from '../../navigation/ProducerNavigation';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';

interface IProps {
  party: GQLParty;
}

const PieceInventoryList: React.FC<IProps> = ({ party }: IProps) => {
  const deliveryUnitId = ProducerNavigation.piecesInventoryList.paramsFromPath(useRouteMatch())
    .deliveryUnitId;
  const [errorComponentState, setErrorComponentState] = useState(false);
  const [selectedPieceInventory, selectPieceInventory] = useState<GQLPieceInventory>();
  const [isUpdatePieceInventoryDialogOpen, setUpdatePieceInventoryDialogOpen] = useState(false);
  const [filteredInventories, setFilteredInventories] = useState('');

  const {
    data,
    loading,
    error,
    networkStatus,
    refetch
  } = GQLOperationBuilder.deliveryUnit.getPieceInventorysByPartyAndDeliveryUnit({
    producerPartyId: party.id,
    deliveryUnitId: deliveryUnitId
  });

  const reloadData = () => {
    return refetch();
  };

  if (loading) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        callback={reloadData}
        message={error.message}
      />
    );

  const columns = [
    'Piece Name',
    'Size Type',
    'Material Type',
    'Plating Type',
    'Inventory Reference',
    'Actions'
  ];

  const handleClickUpdateIcon = (pieceInventory: any) => {
    selectPieceInventory(pieceInventory);
    setUpdatePieceInventoryDialogOpen(true);
  };

  const handleFilterChange = (event: any) => setFilteredInventories(event.target.value);

  const getPieceInventoryRows = () => {
    return GQLOperationBuilder.deliveryUnit
      .pieceInventorysFromResponse(data)
      .filter(pieceInventory => {
        return pieceInventory.pieceTitle.en!.toLowerCase().includes(filteredInventories.toLowerCase().trim());
      })
      .sort((p1, p2) =>
        [
          p1.pieceTitle.en!,
          p1.pieceReference.pieceConfiguration.sizeType,
          p1.pieceReference.pieceConfiguration.platingType
        ]
          .join()
          .localeCompare(
            [
              p2.pieceTitle.en!,
              p2.pieceReference.pieceConfiguration.sizeType,
              p2.pieceReference.pieceConfiguration.platingType
            ].join()
          )
      )
      .map(pieceInventory => {
        return {
          id: pieceInventory.id,
          cells: [
            pieceInventory.pieceTitle.en,
            pieceInventory.pieceReference.pieceConfiguration.sizeType,
            pieceInventory.pieceReference.pieceConfiguration.materialType,
            pieceInventory.pieceReference.pieceConfiguration.platingType,
            pieceInventory.inventoryReference,
            <IconButton aria-label="update" onClick={() => handleClickUpdateIcon(pieceInventory)}>
              <Edit2 />
            </IconButton>
          ]
        };
      });
  };

  return (
    <Fragment>
      <JwlTable
        title="Delivery Unit pieces inventory List"
        description={`The list of all current pieces in the inventory of the delivery unit of ${party.name}. The inventory reference must match exactly the inventory SKU (or some other unique identifier) in the inventory ERP of the delivery unit.`}
        columns={columns}
        rows={getPieceInventoryRows()}
        onFilterChange={handleFilterChange}
      />
      {isUpdatePieceInventoryDialogOpen && (
        <PieceInventoryUpdateDialog
          pieceInventory={selectedPieceInventory!}
          onClose={(persisted: boolean) => {
            setUpdatePieceInventoryDialogOpen(false);
            if (persisted) {
              successNotification(toast, 'Piece inventory data updated successfully', reloadData());
            }
          }}
        />
      )}
    </Fragment>
  );
};

export default PieceInventoryList;
